import cn from 'classnames'
import {FC, ReactNode, useMemo, useState} from 'react'

import {EntityLogo} from '@/components/common/content/EntityLogo'
import {Text, TextVariant} from '@/components/common/content/Text'
import {useFormSummaryContent} from '@/content/formSummary'
import {useTranslation} from '@/i18n'
import {EXPAND_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {TrademarkTypeValue, VerificationTrademarkTypeValue} from '@/utils/constants'
import {DocumentLocation} from '@/utils/document/types'
import {USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {LogoEntityType} from '@/utils/user-portal/types'

import {isLogoTrademark, isWordTrademark} from '../../helpers'
import {FormSummaryTrademarkLogo} from './FormSummaryTrademarkLogo'
import {FormSummaryItem} from './layout/FormSummaryItem'
import {FormSummarySection} from './layout/FormSummarySection'

export type FormSummaryTrademarkPreviewProps = {
  type: TrademarkTypeValue
  typeValue?: VerificationTrademarkTypeValue
  trademarkTextValue?: string
  logoDocument?: DocumentLocation
  uploadedFile?: File
  entityId?: string
  logoEntityType?: LogoEntityType
  helperText?: ReactNode
}

export const FormSummaryTrademarkPreview: FC<FormSummaryTrademarkPreviewProps> = ({
  type,
  typeValue,
  trademarkTextValue,
  logoDocument,
  uploadedFile,
  entityId,
  logoEntityType,
  helperText,
}) => {
  const {t} = useTranslation()
  const {
    trademarkTitle,
    logoTrademarkTitle,
    figurativeTrademarkTitle,
    wordTrademarkTitle,
    combinedTrademarkTitle,
    hideText,
    showText,
  } = useFormSummaryContent()
  const [isTmPreviewExpanded, setIsTmPreviewExpanded] = useState(false)

  const isText = isWordTrademark(type)
  const isImage = isLogoTrademark(type)

  const trademarkTypeTitle =
    type === TrademarkTypeValue.combined
      ? t(combinedTrademarkTitle)
      : type === TrademarkTypeValue.image
      ? typeValue === VerificationTrademarkTypeValue.logo
        ? t(logoTrademarkTitle)
        : t(figurativeTrademarkTitle)
      : t(wordTrademarkTitle)

  const imageFileSrc = useMemo(
    () => (uploadedFile ? URL.createObjectURL(uploadedFile) : undefined),
    [uploadedFile],
  )

  return (
    <>
      <FormSummarySection label={t(trademarkTitle)}>
        <div>
          <FormSummaryItem
            title={trademarkTypeTitle}
            button={
              <button onClick={() => setIsTmPreviewExpanded(!isTmPreviewExpanded)} type="button">
                <Text color={theme.palette.info.main} className="underline" align="right">
                  {isTmPreviewExpanded ? t(hideText) : t(showText)}
                </Text>
              </button>
            }
          />

          <div className={cn('preview-wrapper', isTmPreviewExpanded && 'expanded')}>
            {isImage && (
              <div className="preview">
                {logoEntityType && entityId ? (
                  <EntityLogo
                    logoEntityType={logoEntityType}
                    entityId={entityId}
                    justify="center"
                    maxHeight="128px"
                  />
                ) : (
                  <FormSummaryTrademarkLogo
                    imageFileSrc={imageFileSrc}
                    logoDocument={logoDocument}
                  />
                )}
              </div>
            )}
            {isText && trademarkTextValue && (
              <div className="preview">
                <Text variant={TextVariant.h3} element="p" align="center">
                  {trademarkTextValue}
                </Text>
              </div>
            )}
          </div>
          {isTmPreviewExpanded && helperText}
        </div>
      </FormSummarySection>

      <style jsx>{`
        .preview-wrapper {
          display: flex;
          visibility: hidden;
          opacity: 0;
          min-height: 0;
          max-height: 0;
          padding-left: ${theme.spacing[3]};
          padding-right: ${theme.spacing[3]};
          background-color: ${theme.palette.gray[50]};
          border-radius: 8px;
          flex: 1;
          overflow: hidden;
          transition: all ${EXPAND_TRANSITION}ms ease-in-out;
        }

        .expanded {
          visibility: visible;
          opacity: 1;
          min-height: 160px;
          max-height: 400px;
          padding-top: ${theme.spacing[3]};
          padding-bottom: ${theme.spacing[3]};
          margin-top: ${theme.spacing[3]};
        }

        .preview {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
        }

        .preview + .preview {
          margin-top: ${theme.spacing[3]};
        }

        @media ${USER_TABLET_SCREENSIZE} {
          .expanded {
            min-height: 96px;
            max-height: inherit;
          }
        }
      `}</style>
    </>
  )
}
